import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import { IconButton } from '../components/Button';
import DangerouslySetInnerHTML from '../components/DangerouslySetInnerHTML';
import DeltaPreview from '../components/DeltaPreview';
import Menu, { MenuItem } from '../components/Menu';
import PopOverPrimitive from '../components/PopOverPrimitive';
import SVGIcon from '../components/SVGIcon';
import Slot from '../components/Slot';
import { MentionBaseProvider } from '../editor/mentions/MentionBase';
import alertIcon from '../icons/non-prefilled/16x16/alert.svg';
import copyIcon from '../icons/non-prefilled/16x16/copy.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import retryIcon from '../icons/non-prefilled/16x16/retry.svg';
import PostPill from '../post/PostPill';
import { useAllSlimPostMap } from '../post/useAllSlimPosts';
import getPostIdsFromOps from '../utils/getPostIdsFromOps';
import type {
  GeneratingSystemMessage,
  Message,
  SystemMessage,
  UserMessage,
} from './AiCopilotChatPanel';
import * as styles from './Messages.module.scss';

const DATA_MESSAGE_ID_ATTRIBUTE = 'data-message-id';
const HALF_SOURCES_WIDTH = 139;
export const MESSAGES_LIST_PADDING_BOTTOM = parseInt(styles.paddingBottom, 10);

export const getMessageElementById = (id: string) => {
  return document.querySelector<HTMLElement>(
    `[${DATA_MESSAGE_ID_ATTRIBUTE}="${id}"]`,
  );
};

interface MessageContainerProps {
  message: Message | GeneratingSystemMessage;
  children: React.ReactNode;
  isEditing?: boolean;
}

const MessageContainer = ({
  message,
  children,
  isEditing,
}: MessageContainerProps) => {
  return (
    <article
      className={classNames(
        styles.message,
        styles[message.type],
        'copilot-message',
        { [styles.isEditing]: isEditing },
      )}
      {...{ [DATA_MESSAGE_ID_ATTRIBUTE]: message.id }}
    >
      {children}
    </article>
  );
};

interface PostSourcesProps {
  posts: React.ComponentProps<typeof PostPill>['post'][];
}

const PostPills = ({ posts }: PostSourcesProps) => {
  return (
    <div className={styles.postPills}>
      {posts.map((post) => (
        <PostPill
          size="small"
          key={post.id}
          post={post}
          maxWidth={
            posts && posts.length > 2
              ? HALF_SOURCES_WIDTH
              : 2 * HALF_SOURCES_WIDTH + 8
          }
        />
      ))}
    </div>
  );
};

interface SystemMessageProps {
  message: SystemMessage | GeneratingSystemMessage;
  markdownToHTML: (markdown: string) => string | null;
}

const SystemMessage = ({ message, markdownToHTML }: SystemMessageProps) => {
  const allPostsMap = useAllSlimPostMap();
  if (message.status === 'update' || message.status === 'finished') {
    const sourcePosts =
      message.status === 'finished' && message.sources.length > 0
        ? message.sources.map((source) => allPostsMap[source.id] ?? source)
        : [];
    return (
      <MessageContainer message={message}>
        <DangerouslySetInnerHTML html={markdownToHTML(message.text) ?? ''}>
          <div
            onClick={(e) => {
              const anchor = (
                e.target as HTMLElement
              ).closest<HTMLAnchorElement>('a[href]');

              if (anchor) {
                e.preventDefault();
                window.open(anchor.href, '_blank', 'noreferrer');
              }
            }}
          />
        </DangerouslySetInnerHTML>
        {sourcePosts.length > 0 && <PostPills posts={sourcePosts} />}
      </MessageContainer>
    );
  }

  if (message.status === 'error') {
    return (
      <MessageContainer message={message}>
        <div className={styles.errorMessage}>
          <SVGIcon size={16}>{alertIcon}</SVGIcon>
          {message.error}
        </div>
      </MessageContainer>
    );
  }

  const status = (() => {
    switch (message.status) {
      case 'search':
        return 'Searching';
      case 'reason':
        return 'Thinking hard';
      default:
        return 'Thinking';
    }
  })();

  return (
    <MessageContainer message={message}>
      <div className={styles.loadingIndicator}>
        {status}
        <span className={styles.dots} />
      </div>
    </MessageContainer>
  );
};

const trimMessageContent = (content: Op[]) => {
  return content.map((op, index) => {
    if (typeof op.insert !== 'string') {
      return op;
    }

    if (index === 0) {
      return {
        ...op,
        insert: op.insert.trimStart(),
      };
    }

    if (index === content.length - 1) {
      return {
        ...op,
        insert: op.insert.trimEnd(),
      };
    }

    return op;
  });
};

interface UserMessageOverflowMenuProps {
  onRetry: () => void;
  onRetryInNewChat: () => void;
  onEdit: () => void;
  isGenerating: boolean;
}

const UserMessageOverflowMenu = ({
  onRetry,
  onRetryInNewChat,
  onEdit,
  isGenerating,
}: UserMessageOverflowMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <PopOverPrimitive
      trigger="click"
      placement="bottom-start"
      open={isOpen}
      onOpenChange={setIsOpen}
      offset={6}
      screenPadding={24}
    >
      <PopOverPrimitive.Trigger>
        <IconButton
          className={classNames(styles.overflowMenuButton, {
            [styles.isOpen]: isOpen,
          })}
          active={isOpen}
          aria-label="More options"
          size="xs"
        >
          {overflowMenuIcon}
        </IconButton>
      </PopOverPrimitive.Trigger>
      <PopOverPrimitive.Floating
        noPortal
        className={styles.overflowMenuFloating}
      >
        <PopOverPrimitive.Content>
          <Menu>
            <MenuItem
              icon={retryIcon}
              onClick={() => {
                onRetry();
                setIsOpen(false);
              }}
              disabled={isGenerating}
              tooltip={
                isGenerating ? 'Cannot retry while generating' : undefined
              }
            >
              Retry answer
            </MenuItem>
            <MenuItem
              icon={copyIcon}
              onClick={() => {
                onRetryInNewChat();
                setIsOpen(false);
              }}
              disabled={isGenerating}
              tooltip={
                isGenerating ? 'Cannot retry while generating' : undefined
              }
            >
              Retry in new chat
            </MenuItem>
            <MenuItem
              icon={editIcon}
              onClick={() => {
                onEdit();
                setIsOpen(false);
              }}
              disabled={isGenerating}
              tooltip={
                isGenerating ? 'Cannot edit while generating' : undefined
              }
            >
              Edit message
            </MenuItem>
          </Menu>
        </PopOverPrimitive.Content>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

interface UserMessageProps {
  message: UserMessage;
  overflowMenu: React.ReactNode;
  isEditing: boolean;
}

const UserMessage = ({
  message,
  overflowMenu,
  isEditing,
}: UserMessageProps) => {
  const messageWithoutMentions = trimMessageContent(
    message.content.filter((op) => typeof op.insert === 'string'),
  );

  const postIds = getPostIdsFromOps(message.content);

  const allPostsMap = useAllSlimPostMap();
  const posts = _.compact(postIds.map((id) => allPostsMap[id]));

  return (
    <MessageContainer message={message} isEditing={isEditing}>
      <div className={styles.userMessageContent}>
        <div className={styles.overflowMenuContainer}>{overflowMenu}</div>
        <MentionBaseProvider containerType="copilot">
          <DeltaPreview content={messageWithoutMentions} typography="copilot" />
        </MentionBaseProvider>
      </div>
      {posts.length > 0 && <PostPills posts={posts} />}
    </MessageContainer>
  );
};

interface MessagesListProps {
  messages: (Message | GeneratingSystemMessage)[];
  children: (message: Message | GeneratingSystemMessage) => React.ReactNode;
}

const MessagesList = ({ messages, children }: MessagesListProps) => {
  return (
    <>
      {messages.slice(0, -2).map((message) => (
        <React.Fragment key={message.id}>{children(message)}</React.Fragment>
      ))}
      <div className={styles.lastMessageExchange}>
        {messages.slice(-2).map((message) => (
          <React.Fragment key={message.id}>{children(message)}</React.Fragment>
        ))}
      </div>
    </>
  );
};

interface MessagesContainerProps {
  children: React.ReactNode;
  hasMessages: boolean;
  footerRef: React.RefObject<HTMLDivElement | null>;
}

const MessagesContainer = React.forwardRef<
  HTMLDivElement,
  MessagesContainerProps
>(({ children, hasMessages, footerRef }, ref) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [measurements, setMeasurements] = useState({
    headerHeight: 0,
    footerHeight: 0,
  });

  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const footer = footerRef.current;
    if (!footer) return;

    setMeasurements({
      headerHeight: messagesContainer.offsetTop,
      footerHeight: footer.clientHeight,
    });
  }, [footerRef]);

  return (
    <Slot ref={ref}>
      <div
        ref={messagesContainerRef}
        className={classNames(styles.messagesContainer, {
          [styles.hasMessages]: hasMessages,
        })}
        style={
          {
            '--header-height': `${measurements.headerHeight}px`,
            '--footer-height': `${measurements.footerHeight}px`,
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </Slot>
  );
});

const Messages = {
  SystemMessage,
  UserMessage,
  List: MessagesList,
  Container: MessagesContainer,
  UserMessageOverflowMenu,
};

export default Messages;
